// Shared submit-flow hook for forms that POST/PATCH JSON to a worker
// endpoint and want consistent CSRF spreading, ApiError handling, server-
// error state, and TanStack mutation lifecycle. Lifted from 15 hand-rolled
// `useForm + useMutation + fetch` triples per the DRY-up audit §4.1.
//
// Use this for forms that send `values` directly as the request body. For
// forms that need to reshape values before send (e.g. host/venues uses a
// `toCreatePayload` projection), pass a `transform` callback.

import { useForm, type UseFormInput, type UseFormReturnType } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import type { z } from 'zod';

import { ApiError, mutateJson } from '../lib/api';

export type UseApiFormOptions<Values extends Record<string, unknown>, TResp> = {
  /** Endpoint path, e.g. '/api/auth/sign-in'. */
  endpoint: string;
  /** HTTP verb. Defaults to POST. */
  method?: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * Optional zod schema; wraps via `mantine-form-zod-resolver`. Use either
   * this OR `formOptions.validate` (Mantine's field-level validators) — not
   * both. The schema route is the common case; field-level validators land
   * in `formOptions.validate` for forms with conditional rules that don't
   * map cleanly onto a single zod schema.
   */
  schema?: z.ZodTypeAny;
  /** Initial values for the Mantine form. */
  initialValues: Values;
  /** Mantine useForm options (mode, validate, etc.). */
  formOptions?: Omit<UseFormInput<Values>, 'initialValues'>;
  /** Reshape values before send. Defaults to passing values through. */
  transform?: (values: Values) => unknown;
  /** Runs after a successful response. */
  onSuccess?: (resp: TResp, values: Values) => void | Promise<void>;
  /**
   * Custom error mapper. Return a string to surface as `serverError`, or
   * `null` to skip default handling (caller will manage state). When
   * omitted, the hook surfaces `err.message` from `ApiError` (or any thrown
   * Error) as `serverError`.
   */
  mapError?: (err: Error) => string | null;
};

export type UseApiFormResult<Values extends Record<string, unknown>> = {
  form: UseFormReturnType<Values>;
  submit: () => void;
  submitting: boolean;
  /** Server-side error message to render in an inline Alert. */
  serverError: string | null;
  /** Clear or override the server error from outside the submit flow. */
  setServerError: (msg: string | null) => void;
  /** Underlying ApiError (or generic Error) for callers that need status. */
  lastError: Error | null;
};

export function useApiForm<Values extends Record<string, unknown>, TResp = unknown>(
  opts: UseApiFormOptions<Values, TResp>,
): UseApiFormResult<Values> {
  const { schema, initialValues, formOptions, transform, onSuccess, mapError } = opts;
  const [serverError, setServerError] = useState<string | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);

  const form = useForm<Values>({
    ...formOptions,
    initialValues,
    // `schema` takes precedence over `formOptions.validate` so the
    // schema-only call site (the common case) doesn't need to reach into
    // formOptions. Field-level validators land via formOptions.validate
    // for forms with conditional rules that don't map onto a zod schema.
    ...(schema !== undefined ? { validate: zod4Resolver(schema) } : {}),
  });

  const mutation = useMutation({
    mutationFn: async (values: Values): Promise<TResp> => {
      const body = transform ? transform(values) : values;
      return await mutateJson<TResp>(opts.endpoint, body, {
        method: opts.method ?? 'POST',
      });
    },
    onSuccess: (resp, values) => {
      setLastError(null);
      setServerError(null);
      if (onSuccess) return onSuccess(resp, values);
    },
    onError: (err: Error) => {
      setLastError(err);
      const mapped = mapError ? mapError(err) : err.message;
      if (mapped !== null) setServerError(mapped);
    },
  });

  const submit = form.onSubmit((values) => {
    setServerError(null);
    mutation.mutate(values);
  });

  return {
    form,
    submit: submit as unknown as () => void,
    submitting: mutation.isPending,
    serverError,
    setServerError,
    lastError,
  };
}

export { ApiError };
